import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const SendLoginInviteModal = ({ person, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSendInvite = async () => {
    if (!person?.email) {
      setError('This personnel record has no email address. Please add an email first.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use Supabase admin invite user by email
      const { data, error: inviteError } = await supabase?.auth?.admin?.inviteUserByEmail(
        person?.email,
        {
          data: {
            full_name: person?.name,
            role: person?.role || 'technician',
            personnel_id: person?.id
          },
          redirectTo: `${window.location?.origin}/login`
        }
      );

      if (inviteError) {
        // If admin API not available, fall back to signInWithOtp (magic link)
        if (inviteError?.message?.includes('not allowed') || inviteError?.status === 403) {
          const { error: otpError } = await supabase?.auth?.signInWithOtp({
            email: person?.email,
            options: {
              shouldCreateUser: true,
              emailRedirectTo: `${window.location?.origin}/login`,
              data: {
                full_name: person?.name,
                role: person?.role || 'technician'
              }
            }
          });
          if (otpError) throw otpError;
        } else {
          throw inviteError;
        }
      }

      // If invite succeeded and we have a user ID, link it to personnel record
      if (data?.user?.id) {
        await supabase?.from('personnel')?.update({ auth_user_id: data?.user?.id })?.eq('id', person?.id);
      }

      setSuccess(true);
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Invite error:', err);
      setError(err?.message || 'Failed to send invite. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[1500] flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-5 w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="Mail" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-lg font-heading font-semibold text-foreground">Send Login Invite</h2>
              <p className="text-xs text-muted-foreground">Create app access for this person</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-smooth"
          >
            <Icon name="X" size={20} color="var(--color-foreground)" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 md:p-6 space-y-4">
          {success ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-4">
                <Icon name="CheckCircle" size={32} color="var(--color-success)" />
              </div>
              <h3 className="text-lg font-heading font-semibold text-foreground mb-2">Invite Sent!</h3>
              <p className="text-sm text-muted-foreground">
                A login invite has been sent to <strong>{person?.email}</strong>.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                {person?.name} will receive an email with a link to set their password and access the app.
              </p>
              <Button
                variant="default"
                fullWidth
                onClick={onClose}
                className="mt-6"
              >
                Done
              </Button>
            </div>
          ) : (
            <>
              {/* Personnel info */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <Icon name="User" size={16} color="var(--color-muted-foreground)" />
                  <span className="text-sm font-medium text-foreground">{person?.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="Mail" size={16} color="var(--color-muted-foreground)" />
                  <span className="text-sm text-muted-foreground">{person?.email || 'No email on file'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="Briefcase" size={16} color="var(--color-muted-foreground)" />
                  <span className="text-sm text-muted-foreground capitalize">{person?.role}</span>
                </div>
              </div>

              {/* What happens */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">What this does:</p>
                <ul className="space-y-2">
                  {[
                    'Sends an email invite to ' + (person?.email || 'their email'),
                    'Creates a secure login account for them',
                    'Links their account to this personnel record',
                    'They can then log in and view their assigned tickets'
                  ]?.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Icon name="CheckCircle" size={14} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {error && (
                <div className="bg-error/10 border border-error/30 rounded-lg p-3 flex items-start gap-2">
                  <Icon name="AlertCircle" size={16} color="var(--color-error)" className="mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-error">{error}</p>
                </div>
              )}

              {!person?.email && (
                <div className="bg-warning/10 border border-warning/30 rounded-lg p-3 flex items-start gap-2">
                  <Icon name="AlertTriangle" size={16} color="var(--color-warning)" className="mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-warning">No email address found. Edit this person's profile to add an email before sending an invite.</p>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  fullWidth
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  fullWidth
                  onClick={handleSendInvite}
                  loading={loading}
                  disabled={loading || !person?.email}
                  iconName="Send"
                  iconPosition="left"
                >
                  Send Invite
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SendLoginInviteModal;
